import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Calendar, Video, ArrowRight, ShieldCheck, Sparkles, X } from "lucide-react";

/**
 * ReservationSuccessModal
 * High end Apple Keynote style confirmation modal that pops up after a basket
 * Reserve. Shows: green success header, booked Zoom slot card, 5 step
 * infographic of what happens next, and a Stripe trust strip.
 *
 * Props:
 *   open      bool
 *   onClose   () => void
 *   data      { reservation_id, tier, display_date, display_time, zoom_url, next_steps[] }
 */
export default function ReservationSuccessModal({ open, onClose, data }) {
  if (!data) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          data-testid="reservation-success-modal"
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-slate-950/70 backdrop-blur-md"
            onClick={onClose}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          />

          {/* Card */}
          <motion.div
            className="relative w-full max-w-2xl bg-white rounded-3xl shadow-[0_60px_120px_-30px_rgba(15,23,42,0.45)] overflow-hidden max-h-[92vh] overflow-y-auto"
            initial={{ scale: 0.94, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, y: 10, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Close */}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close confirmation"
              data-testid="reservation-modal-close"
              className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full bg-white/80 hover:bg-white border border-slate-200 flex items-center justify-center text-slate-700 hover:text-slate-900 backdrop-blur-sm transition-all"
            >
              <X size={16} />
            </button>

            {/* Header gradient */}
            <div className="relative bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-500 text-white px-8 sm:px-12 pt-12 pb-10 text-center">
              {/* Subtle gloss */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.2),transparent_55%)] pointer-events-none" />
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 280, damping: 14 }}
                className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 backdrop-blur-md ring-2 ring-white/40 mb-5 shadow-lg"
              >
                <CheckCircle2 size={32} strokeWidth={2.4} className="text-white" />
              </motion.div>
              <p className="text-[11px] font-bold tracking-[0.3em] uppercase text-white/85 mb-2">Order Confirmed</p>
              <h2
                className="text-3xl sm:text-4xl font-black tracking-[-0.025em] leading-[1.05] mb-3"
                style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                data-testid="reservation-modal-headline"
              >
                Welcome to STRATIQ.
              </h2>
              <p className="text-sm sm:text-base text-white/90 max-w-md mx-auto leading-relaxed">
                Reservation <span className="font-bold tracking-wide">{data.reservation_id}</span> secured. Your one to one onboarding call with Aaron is locked into the calendar.
              </p>
            </div>

            {/* Booked slot card */}
            <div className="px-6 sm:px-12 pt-8">
              <div
                className="rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50/50 border border-slate-200 p-5 sm:p-6"
                data-testid="reservation-modal-slot"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-11 h-11 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md">
                    <Calendar size={20} strokeWidth={2.2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-blue-700">Your Onboarding Call</p>
                    <p
                      className="text-lg sm:text-xl font-black tracking-tight text-slate-950 mt-0.5"
                      style={{ fontFamily: "'Inter', system-ui, sans-serif" }}
                    >
                      {data.display_date}
                    </p>
                    <p className="text-sm text-slate-600 mt-0.5">{data.display_time} &middot; 30 minute call with Aaron</p>
                  </div>
                </div>
                <a
                  href={data.zoom_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="reservation-modal-zoom"
                  className="mt-5 inline-flex items-center gap-2 bg-slate-950 hover:bg-blue-600 text-white rounded-full px-6 py-3 text-sm font-bold tracking-wide transition-all duration-300 hover:-translate-y-0.5 w-full sm:w-auto justify-center"
                >
                  <Video size={15} /> Join the Zoom call
                </a>
              </div>
            </div>

            {/* Step infographic */}
            <div className="px-6 sm:px-12 py-8" data-testid="reservation-modal-steps">
              <div className="flex items-center gap-2 mb-5">
                <Sparkles size={14} className="text-blue-600" />
                <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-slate-500">What happens next</p>
              </div>
              <ol className="space-y-4">
                {(data.next_steps || []).map((step, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35 + i * 0.08, duration: 0.4 }}
                    className="flex items-start gap-4"
                    data-testid={`reservation-modal-step-${i}`}
                  >
                    <span className="flex-shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-50 text-blue-700 text-xs font-black ring-2 ring-blue-100">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm sm:text-base font-bold text-slate-950 leading-snug">{step.title}</p>
                      <p className="text-xs sm:text-sm text-slate-600 leading-snug mt-0.5">{step.desc}</p>
                    </div>
                  </motion.li>
                ))}
              </ol>
            </div>

            {/* Stripe trust strip */}
            <div className="mx-6 sm:mx-12 mb-6 sm:mb-8 rounded-2xl bg-slate-950 text-white px-5 sm:px-7 py-5 flex items-start gap-4">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center">
                <ShieldCheck size={18} className="text-blue-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-blue-300 mb-1">Stripe verified &middot; UK based &middot; Real humans</p>
                <p className="text-xs sm:text-sm text-slate-300 leading-snug">
                  No card has been taken. After your onboarding call we send a separate email with a Stripe verified, fully secure payment link to start your subscription. No surprise charges.
                </p>
              </div>
            </div>

            {/* Footer CTA */}
            <div className="px-6 sm:px-12 pb-8 sm:pb-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button
                type="button"
                onClick={onClose}
                data-testid="reservation-modal-done"
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white rounded-full px-7 py-3.5 text-sm font-bold tracking-wide transition-all duration-300 hover:-translate-y-0.5 shadow-[0_10px_30px_-8px_rgba(37,99,235,0.5)] flex-1"
              >
                Brilliant, see you on the call <ArrowRight size={14} />
              </button>
              <a
                href="/smartmatch"
                className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-200 hover:border-slate-400 rounded-full px-7 py-3.5 text-sm font-semibold tracking-wide transition-all duration-300 hover:-translate-y-0.5"
                data-testid="reservation-modal-smartmatch"
              >
                Try SmartMatch first
              </a>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
